import React from 'react';
import { Typography, Tooltip, IconButton } from '@mui/material';
import UserIcon from '@mui/icons-material/Person';
import FileCopyIcon from '@mui/icons-material/FileCopyOutlined';
import ReplayIcon from '@mui/icons-material/ReplayOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ReactMarkdown from 'react-markdown';
import BrandIcon from './BrandIcon';
import {
  Root,
  MessageContainer,
  UserMessage,
  AssistantMessage,
  ClickablePaper,
  UserIconWrapper,
  BrandIconWrapper,
  ScrollToBottomIconWrapper,
  EndIcons,
  Markdown,
  ToolCall,
} from './styles';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';

const TOOL_MESSAGES = {
  'tool-findStudentWithGradesByStudentName': {
    input: 'Searching for student...',
    output: 'Student performance',
  },
  'tool-findStudentWithGradesByUserId': {
    input: 'Searching for student...',
    output: 'Student performance',
  },
  'tool-getAssignmentCompletionRateForEachSection': {
    input: 'Searching completion rates...',
    output: 'Assignment completion rate for each section',
  },
  'tool-getCourseAveragesBelowOrAbovePercentage': {
    input: 'Searching course averages...',
    output: 'Course averages',
  },
  'tool-getStudentAveragesBelowOrAbovePercentages': {
    input: 'Searching course averages...',
    output: 'Course averages',
  },
  'tool-getAssignmentAveragesBelowOrAbovePercentages': {
    input: 'Searching assignment averages...',
    output: 'Assignment averages',
  },
};

function getToolMessage(type, state) {
  const isInput =
    state === 'input-streaming' ||
    state === 'input-available' ||
    state === 'approval-requested' ||
    state === 'approval-responded';
  const isOutput = state === 'output-available';
  const isError =
    state === 'input-error' ||
    state === 'output-error' ||
    state === 'output-denied';
  const config = TOOL_MESSAGES[type];
  if (isError) return 'Search failed';
  if (isInput) return (config && config.input) || 'Working...';
  if (isOutput) return (config && config.output) || 'Done!';
  return null;
}

function replaceStudentIdsWithNames(text, redactedItems) {
  if (!text || !redactedItems || redactedItems.length === 0) {
    return text;
  }

  return text.replace(
    /studentId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi,
    (match, uuid) => {
      const redactedItem = redactedItems.find(
        (item) => item.studentId === uuid,
      );
      if (redactedItem && redactedItem.studentName) {
        return redactedItem.studentName;
      }
      return match; // Return original if no match found
    },
  );
}

function ScrollToBottomButton() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();
  return (
    !isAtBottom && (
      <ScrollToBottomIconWrapper>
        <IconButton
          size="small"
          aria-label="scroll to bottom"
          onClick={() => scrollToBottom()}
          data-test-id="scroll_to_chat_column_bottom"
        >
          <ArrowDownwardIcon />
        </IconButton>
      </ScrollToBottomIconWrapper>
    )
  );
}

function ThinkingMessage() {
  return (
    <AssistantMessage elevation={1}>
      <MessageContainer>
        <BrandIconWrapper>
          <BrandIcon />
        </BrandIconWrapper>
        <div>
          <Typography variant="body2">Thinking...</Typography>
        </div>
      </MessageContainer>
    </AssistantMessage>
  );
}

export default function ChatColumn({
  messages,
  status,
  onSelect,
  onResend,
  onCopy,
  error,
  regenerate,
}) {
  return (
    <StickToBottom
      className={Root.toString()}
      style={{
        height: 'calc(100vh - 125px)',
        overflowY: 'auto',
        width: '100%',
        maxWidth: '100%',
        position: 'relative',
      }}
      resize="smooth"
      initial="smooth"
      role="log"
    >
      <StickToBottom.Content
        style={{
          padding: '16px',
        }}
      >
        {messages.map((message, index) => {
          // User messages remain a single card
          if (message.role === 'user') {
            const redactedItems = message.metadata?.redactedItems || [];
            return (
              <UserMessage key={message.id || `message-${index}`}>
                <MessageContainer>
                  <UserIconWrapper>
                    <UserIcon />
                  </UserIconWrapper>
                  <div>
                    {(message.parts || []).map((part, i) =>
                      part.type === 'text' ? (
                        <Markdown key={i}>
                          <ReactMarkdown>
                            {replaceStudentIdsWithNames(
                              part.text,
                              redactedItems,
                            )}
                          </ReactMarkdown>
                        </Markdown>
                      ) : null,
                    )}
                  </div>
                </MessageContainer>
                <EndIcons>
                  <Tooltip title="Resend" arrow>
                    <IconButton
                      size="small"
                      aria-label="resend"
                      disabled={status !== 'ready'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onResend(message);
                      }}
                      data-test-id="search_sections_bet_resend_prompt"
                      sx={{ mr: 2 }}
                    >
                      <ReplayIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy and edit" arrow>
                    <IconButton
                      size="small"
                      aria-label="copy and edit"
                      disabled={status === 'submitted'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy(message.parts);
                      }}
                      data-test-id="search_sections_bet_copy_and_edit_prompt"
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </Tooltip>
                </EndIcons>
              </UserMessage>
            );
          }

          // Assistant messages: split into separate cards for text and each tool call
          const textParts = (message.parts || []).filter(
            (p) => p.type === 'text',
          );
          const toolParts = (message.parts || []).filter(
            (p) => typeof p.type === 'string' && p.type.startsWith('tool-'),
          );

          const cards = [];

          if (textParts.length === 0 && toolParts.length === 0) {
            cards.push(
              <ThinkingMessage
                key={`${message.id || `message-${index}`}-thinking`}
              />,
            );
          }

          const metaRefs = message.metadata?.dataRefs || [];
          toolParts.forEach((part, tIndex) => {
            const callId = part.toolCallId;
            const text = getToolMessage(part.type, part.state);
            const metaRef = metaRefs[tIndex];
            const dataKey = metaRef?.tool; // backend now stores preferred data keys
            let chatDataId = metaRef?.chatDataId;
            if (!chatDataId && dataKey) {
              const dataPartForTool = (message.parts || []).find(
                (p) =>
                  typeof p.type === 'string' &&
                  p.type.startsWith('data-') &&
                  p.data &&
                  p.data[dataKey],
              );
              chatDataId = dataPartForTool?.id;
            }

            let paperNode = null;
            const setPaperNode = (node) => {
              if (node) paperNode = node;
            };

            const handleOpen = (e) => {
              e.stopPropagation();
              if (paperNode && typeof paperNode.focus === 'function') {
                paperNode.focus();
              }
              if (chatDataId) onSelect?.(message.id, dataKey, chatDataId);
            };

            const handleKeyDown = (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpen(e);
              }
            };

            cards.push(
              <ClickablePaper
                key={`${message.id || `message-${index}`}-tool-${callId || dataKey || 'tool'}-${tIndex}`}
                role="button"
                tabIndex={0}
                aria-label={`Open ${dataKey || 'response'}`}
                onClick={handleOpen}
                onKeyDown={handleKeyDown}
                ref={setPaperNode}
              >
                <MessageContainer>
                  <BrandIconWrapper>
                    <BrandIcon />
                  </BrandIconWrapper>
                  <ToolCall>
                    {text}
                    {chatDataId && (
                      <IconButton
                        size="small"
                        aria-label="open response"
                        onClick={handleOpen}
                        data-test-id={`open_response_${dataKey || 'unknown'}`}
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    )}
                  </ToolCall>
                </MessageContainer>
              </ClickablePaper>,
            );
          });

          if (textParts.length > 0) {
            cards.push(
              <AssistantMessage
                key={`${message.id || `message-${index}`}-text`}
              >
                <MessageContainer>
                  <BrandIconWrapper>
                    <BrandIcon />
                  </BrandIconWrapper>
                  <div>
                    {textParts.map((part, i) => (
                      <Markdown key={i}>
                        <ReactMarkdown>{part.text}</ReactMarkdown>
                      </Markdown>
                    ))}
                  </div>
                </MessageContainer>
              </AssistantMessage>,
            );
          }

          return (
            <React.Fragment key={message.id || `message-${index}`}>
              {cards}
            </React.Fragment>
          );
        })}
        {status === 'submitted' && <ThinkingMessage />}
        {error && (
          <AssistantMessage elevation={1}>
            <MessageContainer>
              <BrandIconWrapper>
                <BrandIcon />
              </BrandIconWrapper>
              <div>
                <Typography variant="body2">An error occurred.</Typography>
              </div>
              <EndIcons>
                <Tooltip title="Regenerate" arrow>
                  <IconButton
                    size="small"
                    aria-label="regenerate"
                    onClick={(e) => {
                      e.stopPropagation();
                      regenerate();
                    }}
                    sx={{ mr: 2 }}
                  >
                    <ReplayIcon />
                  </IconButton>
                </Tooltip>
              </EndIcons>
            </MessageContainer>
          </AssistantMessage>
        )}
      </StickToBottom.Content>
      <ScrollToBottomButton />
    </StickToBottom>
  );
}
